import { Button, FormHelperText } from "@mui/material";
import { NodeConfig } from "../../types/nodes";
import { WorkflowNode } from "../../types/workflow";
import RunConfigField from "./RunConfigField";

type TriggerSetupProps = {
    openConditionsDetails: boolean,
    localConditions: any,
    removeCondition: any,
    setLocalConditions: any,
    responseConfig: any,
    isAnyStepIncomplete: boolean,
    setConditionsOpenDetails: (openDetails: boolean) => void,
    addNewCondition: () => void,
    onSave: (action?: string, cb?: () => void, isIntermittentSave?: boolean) => void,
    workflowNode: WorkflowNode,
    nodeRunConfig: Record<string, string | null>,
    setNodeRunConfigAndRefNodeId: (key: string, value: string, refNodeId?: string) => void,
    nodeConfig: NodeConfig,
    saveConfigField: (key: string) => void,
    chosenAction: string,
    filterText: string,
    countToNameMap: Record<string, string>,
    isFetchingSavedNodeConfig: boolean,
    setOneShotPopperAnchorEl: (e: React.MouseEvent<HTMLElement>) => void,
    action: string,
    service: string,
};

/** Used for line items mapping config. Dotted children of this type are filtered out; value reconstructed from them. */
export const LINE_ITEM_MAPPING_TYPE = 'lineItemMapping';

/** Keys that are dotted children of a line-item-mapping parent (e.g. line_item_mappings.Accounting Date) should not be rendered as separate fields. */
const isChildOfLineItemMappingKey = (key: string, runConfigRenderData: NodeConfig['run_config_render_data']): boolean => {
    if (!runConfigRenderData) return false;
    const dotIdx = key.indexOf('.');
    if (dotIdx <= 0) return false;
    const parentKey = key.slice(0, dotIdx);
    const renderType = runConfigRenderData[parentKey]?.type;
    return renderType === LINE_ITEM_MAPPING_TYPE;
};

/** When backend flattens line_item_mappings, only dotted keys exist. Reconstruct the object for LineItemsConfigField. */
const getDisplayValue = (
    key: string,
    nodeRunConfig: Record<string, string | null>,
    runConfigRenderData: NodeConfig['run_config_render_data']
): string => {
    if (!nodeRunConfig) return '';
    const direct = (nodeRunConfig as Record<string, unknown>)?.[key]?.toString().trim() || '';
    if (direct) return direct;

    const renderType = runConfigRenderData?.[key]?.type;
    if (renderType !== LINE_ITEM_MAPPING_TYPE) return '';

    const prefix = key + '.';
    const reconstructed: Record<string, string> = {};
    for (const k of Object.keys(nodeRunConfig)) {
        if (k.startsWith(prefix)) {
            const subKey = k.slice(prefix.length);
            const val = (nodeRunConfig as Record<string, unknown>)?.[k]?.toString().trim() ?? '';
            if (subKey) reconstructed[subKey] = val;
        }
    }
    return Object.keys(reconstructed).length > 0 ? JSON.stringify(reconstructed) : '';
};

const TriggerSetup = ({
    nodeRunConfig,
    setNodeRunConfigAndRefNodeId,
    nodeConfig,
    responseConfig,
    chosenAction,
    saveConfigField,
    openConditionsDetails,
    localConditions,
    removeCondition,
    setLocalConditions,
    isAnyStepIncomplete,
    setConditionsOpenDetails,
    addNewCondition,
    onSave,
    workflowNode,
    filterText,
    countToNameMap,
    isFetchingSavedNodeConfig,
    setOneShotPopperAnchorEl,
    action,
    service,
}: TriggerSetupProps) => {
    const { run_config_metadata, run_config_render_data } = nodeConfig || {};
    const keysToRender = new Set([
        ...(nodeRunConfig ? Object.keys(nodeRunConfig) : []),
        ...(run_config_render_data ? Object.keys(run_config_render_data) : [])
    ]);

    const shouldRender = keysToRender.size > 0;

    if (!shouldRender) return <FormHelperText>No details for this action</FormHelperText>;

    const getDisplayOrderVal = (value: number | undefined) => {
        if (typeof value === 'number') {
            return value;
        }

        return 100000;
    }

    const sortedKeys = Array.from(keysToRender)
        .filter((k) => !isChildOfLineItemMappingKey(k, run_config_render_data))
        .sort(
            (a, b) => getDisplayOrderVal(run_config_render_data?.[a]?.display_order) - getDisplayOrderVal(run_config_render_data?.[b]?.display_order)
        );

    return (
        <>
            {action === 'Conversation' && service === 'chatgpt' && <div className="mb-4 flex justify-end">
                <Button
                    variant="outlined"
                    onClick={(e) => setOneShotPopperAnchorEl(e)}
                    className="!normal-case"
                >
                    Manage One-Shot Samples
                </Button>
            </div>}

            {sortedKeys.map((key) => (
                <RunConfigField
                    key={key}
                    keyName={key}
                    renderConfig={run_config_render_data?.[key] || { type: 'text' }}
                    refNodeId={run_config_metadata?.[key]?.ref_node_id || ''}
                    setNodeRunConfigAndRefNodeId={setNodeRunConfigAndRefNodeId}
                    responseConfig={responseConfig}
                    displayValue={getDisplayValue(key, nodeRunConfig, run_config_render_data)}
                    saveConfigField={saveConfigField}
                    openConditionsDetails={openConditionsDetails}
                    localConditions={localConditions}
                    removeCondition={removeCondition}
                    setLocalConditions={setLocalConditions}
                    isAnyStepIncomplete={isAnyStepIncomplete}
                    setConditionsOpenDetails={setConditionsOpenDetails}
                    addNewCondition={addNewCondition}
                    onSave={onSave}
                    workflowNode={workflowNode}
                    filterText={filterText}
                    countToNameMap={countToNameMap}
                    isFetchingSavedNodeConfig={isFetchingSavedNodeConfig}
                />
            ))}
        </>
    );
}

export default TriggerSetup;